/**
 * Spec №13/№12 Stage C follow-up — a generic create/edit modal for App Shell
 * records. Renders a form from the entity type's field defs (enum → select,
 * reference → picker of existing rows, date/number/text/…) and calls the
 * synthesised `create_<slug>` / `update_<slug>` verb. Generic: every Tier E Place
 * (Matters, Clients, …) gets create + edit for free. The workflow/status field is
 * omitted on purpose — its transitions are verb-guarded (e.g. close_matter), so
 * it is not editable through the generic form.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { EntityTypeDefinition, FieldDefinition, MutationError } from "../../../lib/types";
import { mcpClient } from "../../../lib/mcp-client";
import { toolNames, pluralize } from "../../../lib/query-keys";
import { ArrangementPicker } from "./ArrangementPicker";
import { ClientCreateFields } from "./ClientCreateFields";
import { FormError } from "./FormError";
import { completeAction, type OnSuccess } from "./action-completion";

/** №13.3 §1 — normalise a thrown value to what FormError renders (a typed
 *  MutationError from the MCP client, or a plain message string). */
function asFormError(e: unknown): MutationError | string {
  if (e && typeof e === "object" && "kind" in e) return e as MutationError;
  return (e as Error)?.message ?? "Something went wrong.";
}

const HIDDEN_FIELDS = new Set(["id"]);
/** №13.1 §2 — fields the client create form drives itself (identity block +
 *  server-defaulted status), so they're excluded from the generic field loop. */
const CLIENT_IDENTITY_FIELDS = new Set(["name", "kind", "given_name", "family_name", "status"]);
const listToolFor = (targetSlug: string) => `list_${pluralize(targetSlug).replace(/-/g, "_")}`;
const labelOf = (row: Record<string, unknown>): string =>
  String(row.name ?? row.title ?? row.donor_name ?? row.subject_names ?? row.code ?? row.id);

export function CreateEntityModal({
  entityType,
  open,
  onClose,
  onSaved,
  existing,
  lockedPrefill,
  heading,
  onSuccess,
  originVerb,
}: {
  entityType: EntityTypeDefinition;
  open: boolean;
  onClose: () => void;
  onSaved: (id: string) => void;
  /** When provided, the modal edits this record (update_<slug>) instead of creating. */
  existing?: Record<string, unknown>;
  /** №13.1 (§1.2) — context args prefilled + LOCKED (rendered as lock chips, merged
   *  into the payload, hidden from the editable field list). Zero per-page wiring. */
  lockedPrefill?: Record<string, unknown>;
  /** Override the modal title (e.g. a contextual "Add disbursement"). */
  heading?: string;
  /** №13.3 §1 — completion contract. When set (a contextual launch), the modal
   *  runs the contract on success: open → route to the new record, stay →
   *  confirm + refresh. Omit for plain list/edit sites (the caller routes). */
  onSuccess?: OnSuccess;
  /** №13.3 §1 — the verb that launched this create (drives reactive invalidation
   *  + the effective on_success default). Defaults to the synthesised create verb. */
  originVerb?: string;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const isEdit = !!existing;
  const workflowField = entityType.workflow?.field ?? (entityType.workflow ? "status" : undefined);
  const lockedKeys = useMemo(() => new Set(Object.keys(lockedPrefill ?? {})), [lockedPrefill]);

  const fields = useMemo(
    () => entityType.fields.filter((f) => !HIDDEN_FIELDS.has(f.name) && f.name !== workflowField && f.type !== "references" && !lockedKeys.has(f.name)),
    [entityType.fields, workflowField, lockedKeys],
  );
  const refFields = useMemo(() => fields.filter((f) => f.type === "reference" && f.target), [fields]);

  // №13.1 §2 — the `client` type gets a bespoke identity block (kind-driven name +
  // search-before-create). Every other type keeps the generic field loop unchanged.
  const isClient = entityType.slug === "contact";
  // №13.3 §2 — the contact form's two shapes (person = given/family parts,
  // organisation = single name) are anchored to the `kind` enum's VALUES derived
  // from the type definition, never hardcoded. The person-shape kind is the
  // field's default (or first choice); the org-shape kind is the other one. A
  // renamed/added kind can no longer silently drift the form (the `individual` bug).
  const kindField = useMemo(() => entityType.fields.find((f) => f.name === "kind"), [entityType.fields]);
  const kindChoices = kindField?.choices ?? [];
  const personKind = String(kindField?.default ?? kindChoices[0] ?? "person");
  const orgKind = kindChoices.find((c) => c !== personKind) ?? "organisation";
  // Body fields rendered by the generic loop. For client, drop the identity fields
  // the ClientCreateFields block owns (name/kind/parts) and the server-defaulted
  // status (M12 — shown only under "Advanced", never as a top-level input).
  const bodyFields = useMemo(
    () => (isClient ? fields.filter((f) => !CLIENT_IDENTITY_FIELDS.has(f.name)) : fields),
    [isClient, fields],
  );
  const statusField = useMemo(
    () => (isClient ? entityType.fields.find((f) => f.name === "status") : undefined),
    [isClient, entityType.fields],
  );

  const refOptions = useQuery({
    queryKey: ["create-modal", "refs", entityType.slug],
    enabled: open && refFields.length > 0,
    staleTime: 10_000,
    queryFn: async () => {
      const out: Record<string, { id: string; label: string }[]> = {};
      for (const f of refFields) {
        try {
          const r = await mcpClient.toolsCall<{ entities: Record<string, unknown>[] }>(listToolFor(f.target!), { fields: ["*"] });
          out[f.name] = ((r.structuredContent?.entities as Record<string, unknown>[]) ?? []).map((row) => ({ id: String(row.id), label: labelOf(row) }));
        } catch {
          out[f.name] = [];
        }
      }
      return out;
    },
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<MutationError | string | null>(null);

  // (Re)seed the form each time it opens — from `existing` in edit mode, else the
  // type's declared field defaults. №13.3 §2 — defaults are DERIVED from the type
  // definition (FieldDefinition.default), never hardcoded literals, so a fresh
  // record can't seed a value the enum doesn't have.
  useEffect(() => {
    if (!open) return;
    setError(null); setPending(false);
    if (existing) {
      const seed: Record<string, string> = {};
      for (const f of fields) {
        const v = existing[f.name];
        if (v != null && v !== "") seed[f.name] = String(v);
      }
      setValues(seed);
    } else {
      const seed: Record<string, string> = {};
      for (const f of entityType.fields) {
        if (f.default != null) seed[f.name] = String(f.default);
      }
      setValues(seed);
    }
  }, [open, existing, fields, entityType.fields]);

  if (!open) return null;

  const set = (name: string, v: string) => setValues((s) => ({ ...s, [name]: v }));
  const missingRequired = fields.filter((f) => f.required && !String(values[f.name] ?? "").trim());

  // №13.1 §2.1 — the client form's requiredness is per-kind (the engine keeps parts
  // optional; the form enforces them). The person shape needs both name parts; an
  // organisation needs the org name. `name` itself is derived, never asked. §13.3
  // §2 — the kind VALUES are derived (personKind/orgKind), never hardcoded.
  const clientKind = values.kind === orgKind ? orgKind : personKind;

  async function submit() {
    if (isClient) {
      if (clientKind === personKind && (!values.given_name?.trim() || !values.family_name?.trim())) {
        setError("Given name and family name are required."); return;
      }
      if (clientKind === orgKind && !values.name?.trim()) {
        setError("Organisation name is required."); return;
      }
      const extraMissing = bodyFields.filter((f) => f.required && !String(values[f.name] ?? "").trim());
      if (extraMissing.length > 0) { setError(`Required: ${extraMissing.map((f) => f.name).join(", ")}`); return; }
    } else if (missingRequired.length > 0) {
      setError(`Required: ${missingRequired.map((f) => f.name).join(", ")}`); return;
    }
    setPending(true); setError(null);
    try {
      const payload: Record<string, unknown> = {};
      if (isClient) {
        // §2.1 — set kind + derive `name`; carry the parts only for an individual.
        payload.kind = clientKind;
        if (clientKind === personKind) {
          payload.name = `${(values.given_name ?? "").trim()} ${(values.family_name ?? "").trim()}`.trim();
          if (values.given_name?.trim()) payload.given_name = values.given_name.trim();
          if (values.family_name?.trim()) payload.family_name = values.family_name.trim();
        } else {
          payload.name = (values.name ?? "").trim();
        }
        if (values.status?.trim()) payload.status = values.status.trim();
        for (const f of bodyFields) {
          const raw = values[f.name];
          if (raw == null || raw === "") continue;
          payload[f.name] = f.type === "number" || f.type === "currency" ? Number(raw) : raw;
        }
      } else {
        for (const f of fields) {
          const raw = values[f.name];
          if (raw == null || raw === "") continue;
          payload[f.name] = f.type === "number" || f.type === "currency" ? Number(raw) : raw;
        }
      }
      // M13 — context-locked args always win (merged last, never clobbered).
      Object.assign(payload, lockedPrefill ?? {});
      const names = toolNames(entityType);
      const rec = isEdit
        ? await mcpClient.updateEntity(names.update, String(existing!.id), payload)
        : await mcpClient.createEntity(names.create, payload);
      // №13.3 §1 — a contextual create runs the completion contract (route/refresh/
      // confirm). Plain list/edit sites keep their own routing via onSaved.
      if (onSuccess && !isEdit) {
        completeAction({
          verb: originVerb ?? names.create,
          onSuccess,
          target: { slug: entityType.slug, id: rec.id },
          qc, navigate,
          message: `${entityType.name} created`,
        });
      } else {
        void qc.invalidateQueries({ queryKey: ["entities", entityType.slug] });
      }
      onSaved(rec.id);
    } catch (e) {
      setError(asFormError(e));
      setPending(false);
    }
  }

  // №13.1 (§2.7) — when a fee-arrangement reference is captured inside a matter
  // context (e.g. the disbursement capture launched from a matter), use the shared
  // invariant-driven picker instead of a flat list of every arrangement.
  const contextMatter = (lockedPrefill?.matter ?? lockedPrefill?.matter_id) as string | undefined;

  const input = (f: FieldDefinition) => {
    const v = values[f.name] ?? "";
    const common = { value: v, onChange: (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => set(f.name, ev.target.value) };
    if (f.type === "reference" && f.target === "fee-arrangement" && contextMatter) {
      return <ArrangementPicker matterId={contextMatter} value={v} onChange={(id) => set(f.name, id)} />;
    }
    if (f.type === "reference") {
      const opts = refOptions.data?.[f.name] ?? [];
      return (
        <select {...common} className="cem-input">
          <option value="">{refOptions.isLoading ? "Loading…" : `Select ${f.target}…`}</option>
          {opts.map((o) => <option key={o.id} value={o.id}>{o.label} ({o.id})</option>)}
        </select>
      );
    }
    if (f.type === "enum") {
      return (
        <select {...common} className="cem-input">
          <option value="">Select…</option>
          {(f.choices ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      );
    }
    if (f.type === "richtext") return <textarea {...common} className="cem-input" rows={3} />;
    const htmlType = f.type === "number" || f.type === "currency" ? "number" : f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : f.type === "email" ? "email" : "text";
    return <input {...common} type={htmlType} className="cem-input" />;
  };

  const overlay: React.CSSProperties = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, fontFamily: "var(--font-sans)" };
  const box: React.CSSProperties = { background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-secondary)", borderRadius: "var(--border-radius-lg)", maxWidth: 480, width: "100%", maxHeight: "85vh", overflow: "auto", padding: 24 };

  return (
    <div style={overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div role="dialog" aria-modal="true" style={box} className="vt">
        <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 16px" }}>{heading ?? `${isEdit ? "Edit" : "New"} ${entityType.name.toLowerCase()}`}</h2>
        {lockedPrefill && Object.keys(lockedPrefill).length > 0 && (
          <div className="stack" style={{ marginBottom: 14 }}>
            {Object.entries(lockedPrefill).map(([k, v]) => (
              <div className="lockrow" key={k}>
                <span className="lk badge badge--soft">{k}</span>
                <span className="val">{String(v ?? "—")}</span>
                <span className="pad">🔒 prefilled from context · not editable</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {isClient && (
            <ClientCreateFields
              values={values}
              setValue={set}
              clientType={entityType}
              editingId={isEdit ? String(existing!.id) : undefined}
            />
          )}
          {(isClient ? bodyFields : fields).map((f) => (
            <label key={f.name} style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12.5 }}>
              <span style={{ color: "var(--color-text-secondary)" }}>{f.name.replace(/_/g, " ")}{f.required ? " *" : ""}</span>
              {input(f)}
            </label>
          ))}
          {isClient && statusField && (
            // M12 — status is server-defaulted (active); shown only, pre-filled,
            // under a collapsed "Advanced" affordance — never a top-level input.
            <details style={{ fontSize: 12.5 }}>
              <summary style={{ cursor: "pointer", color: "var(--color-text-secondary)" }}>Advanced</summary>
              <label style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
                <span style={{ color: "var(--color-text-secondary)" }}>status</span>
                <select className="cem-input" value={values.status ?? "active"} onChange={(e) => set("status", e.target.value)}>
                  {(statusField.choices ?? []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
            </details>
          )}
        </div>
        <FormError error={error} />
        <div className="row" style={{ gap: 8, marginTop: 18 }}>
          <button onClick={onClose} disabled={pending}>Cancel</button>
          <span className="spacer" />
          <button data-variant="primary" onClick={() => void submit()} disabled={pending}>{pending ? "Saving…" : isEdit ? "Save changes" : `Create ${entityType.name.toLowerCase()}`}</button>
        </div>
      </div>
    </div>
  );
}
